import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Cookies from "js-cookie";
import { toast } from "sonner";
import {
  Microphone, Stop, Spinner, FloppyDisk, Fire, Phone, WhatsappLogo,
  EnvelopeSimple, CaretRight, CheckCircle, Check, Clock, Calendar, Users,
  ListChecks, TextAa, ChatCircleDots, Trash, X, Plus, CaretDown, CaretLeft,
  ArrowClockwise, PaperPlaneTilt, Copy, Sparkle, House, Signature, PhoneCall,
  MapPin, Warning, PencilSimple,
} from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import AppShell from "@/components/AppShell";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import SectionOnboarding from "@/components/SectionOnboarding";
import ReopenGuidesBtn from "@/components/ReopenGuidesBtn";
import PullIndicator from "@/components/PullIndicator";
import { usePullToRefresh } from "@/lib/usePullToRefresh";
import MemoRecorder from "@/pages/agenda/MemoRecorder";
import ManualEventModal from "@/pages/agenda/ManualEventModal";
import { OnboardingHero } from "@/pages/agenda/Onboarding";
import { ContactEditor } from "@/pages/Contatti";
import {
  EVENT_TYPES, formatHourMinute, todayISOInRome, weekdayLabel,
  QuickAddModal, AgendaEventRow, WeekView,
} from "@/pages/agenda/CalendarComponents";

const PRIORITY_COLORS = {
  urgente: "bg-red-100 text-red-700 border-red-200",
  alta: "bg-amber-100 text-amber-800 border-amber-200",
  media: "bg-[#EDEAE2] text-[#7C8B6E] border-[#E8E5DE]",
  bassa: "bg-[#FAFAF7] text-[#9A968B] border-[#E8E5DE]",
};

const CHANNEL_ICON = {
  chiamata: Phone,
  whatsapp: WhatsappLogo,
  email: EnvelopeSimple,
  incontro: Calendar,
  interno: ListChecks,
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const target = new Date(d); target.setHours(0, 0, 0, 0);
  const diff = Math.round((target - today) / 86400000);
  if (diff < 0) return `scaduto ${Math.abs(diff)}g fa`;
  if (diff === 0) return "oggi";
  if (diff === 1) return "domani";
  if (diff < 7) return `tra ${diff}g`;
  return d.toLocaleDateString("it-IT", { day: "numeric", month: "short" });
};

const fmtEur = (n) => n ? "€ " + Math.round(n).toLocaleString("it-IT") : "—";


// ── Todo Card ────────────────────────────────────────────────────
function TodoCard({ todo, onUpdate }) {
  const CIcon = CHANNEL_ICON[todo.channel] || ListChecks;
  const isDone = todo.status === "done";
  const isDismissed = todo.status === "dismissed";
  const isOverdue = todo.due_at && new Date(todo.due_at) < new Date() && !isDone;
  // iter 313 r10 — flag per task creati automaticamente da un lead web.
  // L'utente l'ha trovato confuso ("non capivo il da richiamare, perché me
  // l'hai messo?"). Mostrarne l'origine + dare azioni dirette (call/WA)
  // toglie il mistero.
  const isAuto = todo.auto_generated_from === "lead";
  const phone = todo.contact?.phone;
  const phoneClean = phone ? phone.replace(/[^\d+]/g, "") : "";

  const patch = async (body) => {
    try {
      await api.patch(`/agenda/todos/${todo.id}`, body);
      onUpdate();
    } catch (e) {
      toast.error(apiError(e, "Errore"));
    }
  };

  /** DELETE definitivo del to-do (oltre allo "ignorato" via PATCH).
   *  Lo offriamo principalmente sui todo già done/dismissed, oltre che
   *  come scorciatoia rapida con conferma toast. Backend: DELETE /agenda/todos/{id}.
   */
  const remove = () => {
    toast(
      "Eliminare definitivamente questo promemoria?",
      {
        description: "Verrà rimosso dal database. Non si può annullare.",
        action: {
          label: "Elimina",
          onClick: async () => {
            try {
              await api.delete(`/agenda/todos/${todo.id}`);
              toast.success("Promemoria eliminato");
              onUpdate();
            } catch (e) {
              toast.error(apiError(e, "Errore eliminazione"));
            }
          },
        },
        cancel: { label: "Annulla", onClick: () => {} },
        duration: 8000,
      },
    );
  };

  return (
    <div
      className={`border rounded-lg p-3 transition-all ${
        isDone ? "bg-[#F5F4F0] border-[#E8E5DE] opacity-60" :
        isDismissed ? "bg-[#FAFAF7] border-[#E8E5DE] opacity-40" :
        isOverdue ? "bg-red-50 border-red-200" :
        "bg-white border-[#E8E5DE] hover:border-[#C8C5BD]"
      }`}
      data-testid={`todo-card-${todo.id}`}
    >
      <div className="flex items-start gap-2.5">
        <button
          onClick={() => patch({ status: isDone ? "open" : "done" })}
          className="shrink-0 mt-0.5"
          data-testid={`todo-toggle-${todo.id}`}
          aria-label="toggle"
        >
          {isDone ? (
            <CheckCircle size={20} weight="fill" className="text-[#7C8B6E]" />
          ) : (
            <div className="w-5 h-5 rounded-full border-2 border-[#C8C5BD] hover:border-[#7C8B6E] transition-colors" />
          )}
        </button>
        <div className="flex-1 min-w-0">
          <div className={`text-[13.5px] leading-[1.4] ${isDone ? "line-through text-[#9A968B]" : "text-[#1C1C1A]"}`}>
            {todo.title}
          </div>
          <div className="mt-1 flex items-center gap-2 flex-wrap text-[11px] text-[#6B6A65]">
            <span className={`uppercase tracking-[0.08em] font-bold px-1.5 py-0.5 rounded border text-[9px] ${PRIORITY_COLORS[todo.priority] || PRIORITY_COLORS.media}`}>
              {todo.priority}
            </span>
            <span className="inline-flex items-center gap-1">
              <CIcon size={11} className="text-[#7C8B6E]" /> {todo.channel}
            </span>
            {todo.due_at && (
              <span className={`inline-flex items-center gap-1 ${isOverdue ? "text-red-600 font-semibold" : ""}`}>
                <Clock size={11} /> {fmtDate(todo.due_at)}
              </span>
            )}
            {/* iter 313 r10 — pillola gialla "Da richiesta web" sui task
                auto-generated. L'agente capisce subito che è venuto da un
                lead dell'annuncio pubblico (non l'ha messo lui). */}
            {isAuto && (
              <span
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200 text-[9.5px] uppercase tracking-wider font-bold"
                title="Promemoria creato automaticamente quando un cliente ha lasciato i contatti sul tuo annuncio"
                data-testid={`todo-auto-badge-${todo.id}`}
              >
                <Sparkle size={9} weight="fill" /> da lead
              </span>
            )}
          </div>
          {/* iter 313 r10 — Quick actions inline (chiamata + WhatsApp) se c'è
              telefono del contatto. Click su WhatsApp apre `wa.me/{numero}`. */}
          {!isDone && !isDismissed && phoneClean && (
            <div className="mt-2 flex items-center gap-1.5">
              <a
                href={`tel:${phoneClean}`}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#7C8B6E]/10 hover:bg-[#7C8B6E]/20 text-[#7C8B6E] text-[11px] font-semibold transition-colors"
                data-testid={`todo-call-${todo.id}`}
              >
                <PhoneCall size={11} weight="fill" /> Chiama
              </a>
              <a
                href={`https://wa.me/${phoneClean.replace(/^\+/, "")}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#25D366]/10 hover:bg-[#25D366]/20 text-[#128C7E] text-[11px] font-semibold transition-colors"
                data-testid={`todo-wa-${todo.id}`}
              >
                <WhatsappLogo size={11} weight="fill" /> WhatsApp
              </a>
              {todo.property?.title && (
                <span className="text-[10.5px] text-[#9A968B] truncate" title={todo.property.title}>
                  · {todo.property.title}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {!isDone && !isDismissed && (
            <>
              <button
                onClick={() => patch({ snooze_days: 1 })}
                className="p-1 text-[#9A968B] hover:text-[#7C8B6E] transition-colors"
                title="Rimanda di 1 giorno"
                data-testid={`todo-snooze-${todo.id}`}
              >
                <ArrowClockwise size={13} />
              </button>
              <button
                onClick={() => patch({ status: "dismissed" })}
                className="p-1 text-[#9A968B] hover:text-amber-600 transition-colors"
                title="Ignora (resta in storico)"
                data-testid={`todo-dismiss-${todo.id}`}
              >
                <X size={13} />
              </button>
            </>
          )}
          {/* Sempre visibile: DELETE definitivo. Sul mobile evita confusione
              vs dismiss perché il colore è rosso e c'è conferma toast. */}
          <button
            onClick={remove}
            className="p-1 text-[#9A968B] hover:text-red-600 transition-colors"
            title="Elimina definitivamente"
            data-testid={`todo-delete-${todo.id}`}
          >
            <Trash size={13} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Calendar subscription modal ─────────────────────────────────
function CalendarSyncModal({ open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/agenda/calendar-url")
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Errore caricamento URL"))
      .finally(() => setLoading(false));
  }, [open]);

  const copy = async () => {
    if (!data?.http_url) return;
    try { await navigator.clipboard.writeText(data.http_url); toast.success("URL copiato"); } catch {}
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end lg:items-center justify-center p-3 lg:p-6" data-testid="sync-modal">
      <div className="w-full max-w-lg bg-white rounded-t-2xl lg:rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E8E5DE]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#7C8B6E] font-semibold">Sincronizza</div>
            <div className="text-[15px] font-semibold text-[#1C1C1A]">Porta l'agenda su Google/Apple/Outlook</div>
          </div>
          <button onClick={onClose} className="p-1.5 text-[#9A968B] hover:text-[#1C1C1A]" data-testid="sync-close"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-[13px] text-[#6B6A65] leading-[1.55]">
            Sottoscrivi quest'URL una volta sola. Il tuo calendario (Google/Apple/Outlook) si aggiornerà da solo
            ogni 15-60 min con nuovi appuntamenti e modifiche.
          </p>

          {loading && <div className="py-4 text-center"><Spinner size={16} className="animate-spin mx-auto" /></div>}

          {data && (
            <>
              <div className="bg-[#FAFAF7] border border-[#E8E5DE] rounded-md p-3 flex items-center gap-2" data-testid="sync-url-box">
                <div className="text-[11px] text-[#6B6A65] break-all flex-1 font-mono leading-tight">{data.http_url}</div>
                <button onClick={copy} className="shrink-0 inline-flex items-center gap-1 bg-white border border-[#E8E5DE] rounded px-2 py-1 text-[11px] hover:bg-[#FAFAF7]" data-testid="sync-copy"><Copy size={11} /> Copia</button>
              </div>

              <div className="space-y-3">
                <div className="border border-[#E8E5DE] rounded-lg p-3" data-testid="sync-google">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-5 h-5 rounded bg-gradient-to-br from-blue-500 to-green-500 text-white font-bold text-[11px] flex items-center justify-center">G</div>
                    <div className="text-[13px] font-semibold text-[#1C1C1A]">Google Calendar</div>
                  </div>
                  <div className="text-[11.5px] text-[#6B6A65] leading-[1.5] mb-2">
                    Apri Google Calendar → + (in basso a sinistra) → Da URL → incolla l'URL sopra.
                  </div>
                  <a href={data.google_calendar_add_url} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1.5 bg-[#4285F4] hover:bg-[#3572d8] text-white text-[12px] font-medium rounded-md px-3 py-1.5 transition-colors"
                    data-testid="sync-google-btn">
                    Apri Google Calendar
                  </a>
                </div>

                <div className="border border-[#E8E5DE] rounded-lg p-3" data-testid="sync-apple">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-5 h-5 rounded bg-black text-white font-bold text-[11px] flex items-center justify-center"></div>
                    <div className="text-[13px] font-semibold text-[#1C1C1A]">Apple Calendar (iPhone/Mac)</div>
                  </div>
                  <div className="text-[11.5px] text-[#6B6A65] leading-[1.5] mb-2">
                    Tocca il link qui sotto — iOS/macOS ti proporrà di sottoscrivere l'agenda.
                  </div>
                  <a href={data.webcal_url}
                    className="inline-flex items-center gap-1.5 bg-[#1C1C1A] hover:bg-black text-white text-[12px] font-medium rounded-md px-3 py-1.5 transition-colors"
                    data-testid="sync-apple-btn">
                    Sottoscrivi su iPhone/Mac
                  </a>
                </div>

                <div className="border border-[#E8E5DE] rounded-lg p-3" data-testid="sync-outlook">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-5 h-5 rounded bg-[#0078D4] text-white font-bold text-[11px] flex items-center justify-center">O</div>
                    <div className="text-[13px] font-semibold text-[#1C1C1A]">Outlook</div>
                  </div>
                  <div className="text-[11.5px] text-[#6B6A65] leading-[1.5]">
                    Outlook.com → Calendario → Aggiungi calendario → Sottoscrivi dal Web → incolla l'URL.
                  </div>
                </div>
              </div>

              <div className="text-[10.5px] text-[#9A968B] border-t border-[#EDEAE2] pt-3 leading-[1.55]">
                ℹ Ogni appuntamento include <strong>promemoria automatico 30 min prima</strong> (notifica dal tuo calendario telefono/PC).
                Solo tu vedi la tua agenda — l'URL è privato, non condividerlo.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function TabOggi({ briefing, onUpdate, onboarding, onTryExample }) {
  const [viewMode, setViewMode] = useState("day"); // "day" | "week"
  const [dateStr, setDateStr] = useState(() => todayISOInRome());
  const [day, setDay] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [syncOpen, setSyncOpen] = useState(false);

  const loadDay = async () => {
    try {
      const { data } = await api.get(`/agenda/day?date=${dateStr}`);
      setDay(data);
    } catch { /* silent */ }
  };
  useEffect(() => { loadDay(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [dateStr]);

  const shiftDay = (delta) => {
    const [y, m, d] = dateStr.split("-").map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d + delta));
    const ny = dt.getUTCFullYear();
    const nm = String(dt.getUTCMonth() + 1).padStart(2, "0");
    const nd = String(dt.getUTCDate()).padStart(2, "0");
    setDateStr(`${ny}-${nm}-${nd}`);
  };

  const goToday = () => { setDateStr(todayISOInRome()); };

  if (!briefing || !day) {
    return <div className="py-10 text-center text-[13px] text-[#9A968B]"><Spinner size={18} className="animate-spin mx-auto" /></div>;
  }
  if (onboarding) {
    return <OnboardingHero onTryExample={onTryExample} />;
  }

  const isToday = day.is_today;
  const dateLabel = weekdayLabel(day.date);
  const summary = day.summary || { total: 0, by_type: {} };
  const { hot_contacts = [], drifting_contacts = [] } = briefing;

  return (
    <div className="space-y-5" data-testid="tab-oggi">
      {/* Mini onboarding "Agenda in 3 strade" — visibile solo la prima volta */}
      <SectionOnboarding
        storageKey="agenda:3-strade"
        overline="Come funziona l'agenda"
        title={<>Dalla <span className="italic-accent text-[#7C8B6E]">voce</span> al <span className="italic-accent text-[#7C8B6E]">prossimo passo</span></>}
        subtitle="Dimentica i post-it. Parli, Pronto trasforma la nota in attività · ti dice chi chiamare per primo · ti ricorda chi sta scomparendo."
        steps={[
          {
            key: "voce",
            Icon: Microphone,
            overline: "1 · Voce",
            title: "Detta la nota",
            body: "Dopo il sopralluogo apri la registrazione · diciamo 'Luca Ferrari vuole rivedere giovedì'.",
            accent: "#7C8B6E",
          },
          {
            key: "hot",
            Icon: Fire,
            overline: "2 · Hot",
            title: "Chi chiamare subito",
            body: "Pronto evidenzia i contatti più caldi · interessi recenti + offerte aperte.",
            accent: "#C04A2A",
          },
          {
            key: "drift",
            Icon: Clock,
            overline: "3 · Da non perdere",
            title: "Chi sta scomparendo",
            body: "Contatti silenziosi da 14+ giorni · un messaggio veloce per non perderli.",
            accent: "#8B5E7C",
          },
        ]}
      />

      {/* View switch + sync */}
      <div className="flex items-center gap-2">
        <div className="inline-flex items-center bg-[#F5F4F0] border border-[#E8E5DE] rounded-md p-0.5" data-testid="view-switch">
          {[["day", "Giorno"], ["week", "Settimana"]].map(([k, l]) => (
            <button key={k} onClick={() => setViewMode(k)}
              className={`px-3 py-1 text-[11.5px] font-medium rounded transition-all ${viewMode === k ? "bg-white text-[#1C1C1A] shadow-sm" : "text-[#6B6A65]"}`}
              data-testid={`view-${k}`}>{l}</button>
          ))}
        </div>
        <button onClick={() => setSyncOpen(true)} className="ml-auto inline-flex items-center gap-1.5 text-[11.5px] text-[#7C8B6E] hover:text-[#5A6B4D] font-medium border border-[#E8E5DE] rounded-md px-2.5 py-1 bg-white hover:bg-[#FAFAF7] transition-colors" data-testid="sync-open-btn">
          <Calendar size={12} weight="fill" /> Sincronizza calendario
        </button>
      </div>

      {viewMode === "week" ? (
        <WeekView onUpdate={(date) => { setDateStr(date); setViewMode("day"); }} />
      ) : (
        <>

      {/* Header: date navigator */}
      <div className="bg-white border border-[#E8E5DE] rounded-lg p-3 flex items-center gap-2" data-testid="day-header">
        <button onClick={() => shiftDay(-1)} className="p-1.5 text-[#6B6A65] hover:text-[#1C1C1A] hover:bg-[#FAFAF7] rounded-md transition-colors" aria-label="giorno precedente" data-testid="day-prev">
          <CaretLeft size={16} />
        </button>
        <div className="flex-1 text-center">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#9A968B] font-semibold">{isToday ? "Oggi" : ""}</div>
          <div className="text-[15px] font-serif text-[#1C1C1A] capitalize" data-testid="day-label">{dateLabel}</div>
        </div>
        <button onClick={() => shiftDay(1)} className="p-1.5 text-[#6B6A65] hover:text-[#1C1C1A] hover:bg-[#FAFAF7] rounded-md transition-colors" aria-label="giorno successivo" data-testid="day-next">
          <CaretRight size={16} />
        </button>
        {!isToday && (
          <button onClick={goToday} className="ml-1 text-[11px] text-[#7C8B6E] hover:underline font-medium" data-testid="day-today">Oggi</button>
        )}
        <button onClick={() => setAddOpen(true)} className="ml-1 inline-flex items-center gap-1 bg-[#1C1C1A] hover:bg-black text-white text-[11.5px] font-medium px-2.5 py-1.5 rounded-md transition-colors" data-testid="day-add">
          <Plus size={11} weight="bold" /> Aggiungi
        </button>
      </div>

      {/* Summary line */}
      {summary.total > 0 ? (
        <div className="flex items-center flex-wrap gap-1.5 text-[11.5px]" data-testid="day-summary">
          <span className="text-[#6B6A65]">Hai:</span>
          {Object.entries(summary.by_type).map(([k, n]) => {
            const ev = EVENT_TYPES[k] || EVENT_TYPES.promemoria;
            return (
              <span key={k} className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full ${ev.color}`}>
                {n} {ev.label.toLowerCase()}{n > 1 ? "i" : ""}
              </span>
            );
          })}
        </div>
      ) : (
        <div className="bg-white border border-[#E8E5DE] rounded-xl p-6 text-center" data-testid="day-empty">
          <Calendar size={32} weight="duotone" className="mx-auto text-[#7C8B6E] opacity-40 mb-2" />
          <div className="text-[14px] font-semibold text-[#1C1C1A]">
            {isToday ? "Giornata libera" : `Niente in agenda per ${dateLabel}`}
          </div>
          <div className="text-[12px] text-[#6B6A65] mt-1">
            Parla con Pronto (microfono verde) o usa "Aggiungi" per mettere qualcosa.
          </div>
        </div>
      )}

      {/* Timeline */}
      {day.scheduled.length > 0 && (
        <div className="relative" data-testid="day-timeline">
          {/* Vertical line */}
          <div className="absolute left-[66px] top-2 bottom-2 w-px bg-[#E8E5DE]" />
          <div className="relative space-y-0">
            {day.scheduled.map((t) => <AgendaEventRow key={t.id} todo={t} onUpdate={() => { loadDay(); onUpdate(); }} />)}
          </div>
        </div>
      )}

      {/* Unscheduled (only on today) */}
      {day.unscheduled.length > 0 && (
        <section data-testid="section-unscheduled">
          <h3 className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#9A968B] mb-2 mt-2">Senza orario · {day.unscheduled.length}</h3>
          <div className="space-y-1.5">
            {day.unscheduled.map((t) => <TodoCard key={t.id} todo={t} onUpdate={() => { loadDay(); onUpdate(); }} />)}
          </div>
        </section>
      )}

      {/* Clienti caldi — secondaria */}
      {isToday && hot_contacts.length > 0 && (
        <section data-testid="section-hot" className="pt-2">
          <h3 className="text-[11px] uppercase tracking-[0.22em] font-semibold text-amber-700 mb-2 flex items-center gap-1.5">
            <Fire size={12} weight="fill" /> Da seguire oggi · {hot_contacts.length}
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
            {hot_contacts.slice(0, 4).map((c) => (
              <Link key={c.id} to={`/dashboard/agenda?contact=${c.id}`} className="bg-gradient-to-br from-amber-50 to-white border border-amber-200 rounded-lg p-3 hover:shadow-sm transition-all" data-testid={`hot-${c.id}`}>
                <div className="text-[13.5px] font-semibold text-[#1C1C1A] break-words leading-tight">{c.name}</div>
                <div className="text-[11.5px] text-[#6B6A65] mt-0.5">
                  {c.role} {c.budget_max ? `· fino a ${fmtEur(c.budget_max)}` : ""} {c.zones?.length ? `· ${c.zones[0]}` : ""}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {isToday && drifting_contacts.length > 0 && (
        <section data-testid="section-drifting">
          <h3 className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#6B6A65] mb-2">
            Stanno raffreddandosi · {drifting_contacts.length}
          </h3>
          <div className="space-y-1.5">
            {drifting_contacts.slice(0, 3).map((c) => (
              <Link key={c.id} to={`/dashboard/agenda?contact=${c.id}`} className="flex items-center gap-2.5 p-2.5 bg-white border border-[#E8E5DE] rounded-lg hover:border-[#C8C5BD] text-[12.5px]" data-testid={`drifting-${c.id}`}>
                <div className="w-8 h-8 rounded-full bg-[#EDEAE2] flex items-center justify-center text-[#7C8B6E] font-semibold text-[10.5px] shrink-0">
                  {(c.name || "?").split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[#1C1C1A] font-medium truncate">{c.name}</div>
                  <div className="text-[10.5px] text-[#9A968B]">Non lo senti da {Math.round((new Date() - new Date(c.last_contact_at)) / 86400000)}g</div>
                </div>
                <CaretRight size={12} className="text-[#9A968B] shrink-0" />
              </Link>
            ))}
          </div>
        </section>
      )}

      <QuickAddModal open={addOpen} onClose={() => setAddOpen(false)} defaultDate={dateStr} onCreated={() => { loadDay(); onUpdate(); }} />
        </>
      )}
      <CalendarSyncModal open={syncOpen} onClose={() => setSyncOpen(false)} />
    </div>
  );
}

// ── Tab: Clienti ─────────────────────────────────────────────────
function TabClienti({ contacts, onRefresh, selected, setSelected }) {
  const [filter, setFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const filtered = contacts.filter((c) => {
    if (roleFilter !== "all" && c.role !== roleFilter) return false;
    if (filter && !c.name.toLowerCase().includes(filter.toLowerCase()) &&
        !(c.zones || []).some((z) => z.toLowerCase().includes(filter.toLowerCase()))) return false;
    return true;
  });

  if (selected) {
    return <ContactDetail cid={selected} onBack={() => setSelected(null)} onRefresh={onRefresh} />;
  }

  return (
    <div data-testid="tab-clienti">
      <div className="flex items-center gap-2 mb-3">
        <input
          type="text"
          placeholder="Cerca per nome o zona…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="flex-1 px-3 py-2 bg-white border border-[#E8E5DE] rounded-md text-[13px] focus:outline-none focus:border-[#7C8B6E]"
          data-testid="contacts-search"
        />
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="px-2.5 py-2 bg-white border border-[#E8E5DE] rounded-md text-[12.5px]"
          data-testid="contacts-role-filter"
        >
          <option value="all">Tutti</option>
          <option value="acquirente">Acquirenti</option>
          <option value="venditore">Venditori</option>
          <option value="misto">Misto</option>
        </select>
      </div>
      {filtered.length === 0 ? (
        <div className="py-10 text-center text-[13px] text-[#9A968B]" data-testid="contacts-empty">
          Nessun contatto. Parla con Pronto per aggiungerli automaticamente.
        </div>
      ) : (
        <div className="space-y-1.5">
          {filtered.map((c) => (
            <button
              key={c.id}
              onClick={() => setSelected(c.id)}
              className="w-full text-left flex items-center gap-2.5 p-3 bg-white border border-[#E8E5DE] rounded-lg hover:border-[#C8C5BD] hover:shadow-sm transition-all"
              data-testid={`contact-row-${c.id}`}
            >
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#7C8B6E] to-[#5A6B4D] text-white flex items-center justify-center font-semibold text-[12px] shrink-0">
                {(c.name || "?").split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                {/* iter v29 r221 — User M1547: «in Agenda non vedo il cognome,
                    mostra "Ro..." invece di "Rossi"». Il nome ora occupa tutta
                    la larghezza con la data sotto, non più a fianco. */}
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-[14px] font-semibold text-[#1C1C1A] truncate flex-1">{c.name}</span>
                  <span className="text-[10.5px] text-[#9A968B] shrink-0">
                    {c.last_contact_at ? fmtDate(c.last_contact_at) : "—"}
                  </span>
                </div>
                <div className="text-[11.5px] text-[#6B6A65] truncate">
                  {c.role} {c.budget_max ? `· ${fmtEur(c.budget_max)}` : ""} {c.zones?.length ? `· ${c.zones.slice(0, 2).join(", ")}` : ""} {c.must_have?.length ? `· ${c.must_have.slice(0, 2).join(", ")}` : ""}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ContactDetail({ cid, onBack, onRefresh }) {
  const [data, setData] = useState(null);
  const [waDraft, setWaDraft] = useState(null);
  const [generating, setGenerating] = useState(false);
  // Contatto in formato /contacts/{cid} (con preferences strutturate) per
  // l'editor importato da Contatti.jsx. Caricato lazy quando si apre Modifica.
  const [editorContact, setEditorContact] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  // iter v33 r37 · User: «Non posso eliminare un nome dalla rubrica».
  // Root cause: il toast di sonner con `action` non registrava sempre il
  // click su iOS Capacitor (bug noto: la callback async si perde se il
  // toast viene destroyato prima dell'await). Sostituito con AlertDialog
  // di shadcn (basato su Radix) che ha bottoni HTML nativi affidabili.
  const [confirmDelOpen, setConfirmDelOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/agenda/contacts/${cid}`);
      setData(data);
    } catch { onBack(); }
  };
  useEffect(() => { load(); }, [cid]);

  const openEditor = async () => {
    try {
      // Recupera il contatto col format completo (preferences{}) usato
      // dall'editor di /contatti. Stessa collection, schema diverso.
      const { data: full } = await api.get(`/contacts/${cid}`);
      setEditorContact(full);
      setEditorOpen(true);
    } catch (e) {
      toast.error(apiError(e, "Errore apertura modifica"));
    }
  };

  const generateWa = async () => {
    setGenerating(true); setWaDraft(null);
    try {
      const { data } = await api.post(`/agenda/whatsapp-draft/${cid}`);
      setWaDraft(data);
    } catch (e) {
      toast.error(apiError(e, "Errore generazione"));
    } finally {
      setGenerating(false);
    }
  };

  const copyDraft = async () => {
    if (!waDraft?.text) return;
    try { await navigator.clipboard.writeText(waDraft.text); toast.success("Copiato!"); } catch {}
  };

  const deleteContact = () => {
    // Apre la conferma via AlertDialog (Radix, HTML nativo affidabile su iOS)
    setConfirmDelOpen(true);
  };

  const confirmDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await api.delete(`/agenda/contacts/${cid}`);
      toast.success("Contatto eliminato");
      setConfirmDelOpen(false);
      onBack();
      onRefresh();
    } catch (e) {
      toast.error(apiError(e, "Errore eliminazione"));
    } finally {
      setDeleting(false);
    }
  };

  if (!data) return <div className="py-10 text-center"><Spinner size={18} className="animate-spin mx-auto text-[#7C8B6E]" /></div>;
  const c = data.contact;

  return (
    <div data-testid="contact-detail">
      <button onClick={onBack} className="flex items-center gap-1 text-[12px] text-[#9A968B] hover:text-[#1C1C1A] mb-3" data-testid="contact-back">
        <CaretLeft size={12} /> Tutti i clienti
      </button>

      <div className="bg-white border border-[#E8E5DE] rounded-xl p-5 mb-4">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#7C8B6E] to-[#5A6B4D] text-white flex items-center justify-center font-serif text-[18px] shrink-0">
            {(c.name || "?").split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="text-[22px] font-serif text-[#1C1C1A]" data-testid="contact-name">{c.name}</h2>
            </div>
            <div className="text-[12px] text-[#6B6A65] capitalize">{c.role}</div>
          </div>
          <button onClick={openEditor} className="p-1.5 text-[#9A968B] hover:text-[#7C8B6E] transition-colors" data-testid="contact-edit" aria-label="modifica"><PencilSimple size={14} /></button>
          <button onClick={deleteContact} className="p-1.5 text-[#9A968B] hover:text-red-600 transition-colors" data-testid="contact-delete" aria-label="elimina"><Trash size={14} /></button>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 text-[12.5px]">
          {c.phone && <div><span className="text-[#9A968B]">Telefono: </span><span className="text-[#1C1C1A]">{c.phone}</span></div>}
          {c.email && <div><span className="text-[#9A968B]">Email: </span><span className="text-[#1C1C1A]">{c.email}</span></div>}
          {c.budget_max && <div><span className="text-[#9A968B]">Budget: </span><span className="text-[#1C1C1A]">{c.budget_min ? fmtEur(c.budget_min) + "–" : ""}{fmtEur(c.budget_max)}</span></div>}
          {c.zones?.length > 0 && <div><span className="text-[#9A968B]">Zone: </span><span className="text-[#1C1C1A]">{c.zones.join(", ")}</span></div>}
          {c.must_have?.length > 0 && <div className="col-span-2"><span className="text-[#9A968B]">Must-have: </span><span className="text-[#1C1C1A]">{c.must_have.join(", ")}</span></div>}
          {c.objections?.length > 0 && <div className="col-span-2"><span className="text-[#9A968B]">Obiezioni: </span><span className="text-[#1C1C1A]">{c.objections.join(", ")}</span></div>}
        </div>
        <div className="mt-4 flex gap-2">
          {c.phone && (
            <a
              href={`tel:${c.phone}`}
              className="inline-flex items-center justify-center gap-1.5 bg-[#1C1C1A] hover:bg-black text-white text-[13px] font-medium px-3 py-2.5 rounded-md transition-colors"
              data-testid="contact-call-btn"
              aria-label="Chiama"
            >
              <PhoneCall size={13} weight="fill" /> Chiama
            </a>
          )}
          {c.phone && (
            <a
              href={`https://wa.me/${(c.phone || "").replace(/[^\d]/g, "").replace(/^(?!39)/, "39")}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-1.5 bg-white border border-[#25D366] text-[#1faa53] hover:bg-[#F0FDF4] text-[13px] font-medium px-3 py-2.5 rounded-md transition-colors"
              data-testid="contact-wa-quick"
              aria-label="Apri WhatsApp"
              title="Apri WhatsApp con questo contatto"
            >
              <WhatsappLogo size={13} weight="fill" />
            </a>
          )}
          <button onClick={generateWa} disabled={generating} className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#25D366] hover:bg-[#1faa53] disabled:opacity-60 text-white text-[13px] font-medium py-2.5 rounded-md transition-colors" data-testid="contact-generate-wa">
            {generating ? <Spinner size={13} className="animate-spin" /> : <Sparkle size={13} weight="fill" />}
            {generating ? "Compongo…" : "Crea bozza WhatsApp"}
          </button>
        </div>
      </div>

      {waDraft && (
        <div className="bg-[#DCF8C6] border border-[#B8D9A0] rounded-lg p-4 mb-4 relative" data-testid="wa-draft">
          <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#5A6B4D] mb-2">Bozza WhatsApp</div>
          <div className="text-[13.5px] text-[#1C1C1A] leading-[1.55] whitespace-pre-wrap">{waDraft.text}</div>
          <div className="mt-3 flex gap-2">
            <button onClick={copyDraft} className="inline-flex items-center gap-1 text-[12px] bg-white border border-[#B8D9A0] rounded-md px-2.5 py-1.5 hover:bg-[#FAFAF7]" data-testid="wa-copy"><Copy size={11} /> Copia</button>
            {waDraft.whatsapp_link && (
              <a href={waDraft.whatsapp_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[12px] bg-[#25D366] text-white rounded-md px-2.5 py-1.5 hover:bg-[#1faa53]" data-testid="wa-open">
                <WhatsappLogo size={11} weight="fill" /> Apri WhatsApp
              </a>
            )}
          </div>
        </div>
      )}

      {data.todos.length > 0 && (
        <section className="mb-4" data-testid="contact-todos">
          <h3 className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#7C8B6E] mb-2">Promemoria attivi · {data.todos.filter(t => t.status === "open").length}</h3>
          <div className="space-y-1.5">
            {data.todos.filter((t) => t.status !== "dismissed").map((t) => <TodoCard key={t.id} todo={t} onUpdate={load} />)}
          </div>
        </section>
      )}

      <section data-testid="contact-timeline">
        <h3 className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#6B6A65] mb-2">Storia · {data.memos.length} memo</h3>
        <div className="space-y-2">
          {data.memos.length === 0 && <div className="text-[12.5px] text-[#9A968B]">Nessun memo legato a questo cliente.</div>}
          {data.memos.map((m) => (
            <div key={m.id} className="bg-white border border-[#E8E5DE] rounded-lg p-3">
              <div className="flex items-center justify-between text-[10.5px] text-[#9A968B] uppercase tracking-[0.1em] mb-1.5">
                <span>{new Date(m.created_at).toLocaleString("it-IT", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                <span className="italic normal-case">{m.mood}</span>
              </div>
              <div className="text-[13.5px] text-[#1C1C1A] font-medium">{m.summary}</div>
              <details className="mt-1">
                <summary className="text-[11px] text-[#9A968B] cursor-pointer">vedi trascrizione</summary>
                <div className="mt-1.5 text-[12px] text-[#6B6A65] leading-[1.55] whitespace-pre-wrap">{m.transcript}</div>
              </details>
            </div>
          ))}
        </div>
      </section>

      {editorOpen && editorContact && (
        <ContactEditor
          contactId={cid}
          contact={editorContact}
          onClose={() => setEditorOpen(false)}
          onSaved={() => {
            setEditorOpen(false);
            load();         // ricarica formato Agenda
            onRefresh?.();  // ricarica lista esterna
          }}
        />
      )}

      {/* iter v33 r37 · Dialogo di conferma eliminazione (HTML nativo Radix,
          affidabile su iOS dove il sonner action toast non registrava click) */}
      <AlertDialog open={confirmDelOpen} onOpenChange={setConfirmDelOpen}>
        <AlertDialogContent data-testid="contact-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare {c?.name || "questo contatto"}?</AlertDialogTitle>
            <AlertDialogDescription>
              Rimuoverò il cliente dalla rubrica e non potrai recuperarlo. Memo e promemoria collegati restano.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={deleting}
              data-testid="contact-delete-cancel"
            >
              Annulla
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={deleting}
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-500"
              data-testid="contact-delete-confirm"
            >
              {deleting ? "Elimino…" : "Elimina"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ── Tab: Promemoria ──────────────────────────────────────────────
function TabPromemoria({ onUpdate }) {
  const [todos, setTodos] = useState([]);
  const [status, setStatus] = useState("open");

  const load = async () => {
    try {
      const { data } = await api.get(`/agenda/todos?status=${status}`);
      setTodos(data || []);
    } catch (e) { toast.error(apiError(e, "Errore")); }
  };
  useEffect(() => { load(); }, [status]);

  const handleUpdate = () => { load(); onUpdate(); };

  return (
    <div data-testid="tab-promemoria">
      <div className="flex gap-1 mb-3">
        {[["open","Aperti"],["done","Fatti"],["dismissed","Archiviati"],["all","Tutti"]].map(([v,l]) => (
          <button
            key={v}
            onClick={() => setStatus(v)}
            className={`px-3 py-1.5 rounded-full text-[12px] font-medium transition-colors ${
              status === v ? "bg-[#1C1C1A] text-white" : "bg-white border border-[#E8E5DE] text-[#6B6A65] hover:border-[#7C8B6E]"
            }`}
            data-testid={`todos-filter-${v}`}
          >{l}</button>
        ))}
      </div>
      {todos.length === 0 ? (
        <div className="py-10 text-center text-[13px] text-[#9A968B]" data-testid="todos-empty">Nulla qui.</div>
      ) : (
        <div className="space-y-1.5">
          {todos.map((t) => <TodoCard key={t.id} todo={t} onUpdate={handleUpdate} />)}
        </div>
      )}
    </div>
  );
}

// ── Tab: Timeline ────────────────────────────────────────────────
function TabTimeline() {
  const [memos, setMemos] = useState([]);
  const load = async () => {
    try {
      const { data } = await api.get("/agenda/timeline?limit=50");
      setMemos(data || []);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="tab-timeline">
      {memos.length === 0 ? (
        <div className="py-10 text-center text-[13px] text-[#9A968B]" data-testid="timeline-empty">Nessun memo ancora.</div>
      ) : (
        <div className="space-y-2">
          {memos.map((m) => (
            <MemoRow key={m.id} memo={m} onChanged={load} />
          ))}
        </div>
      )}
    </div>
  );
}

/** Riga della timeline con due controlli inline:
 *   • ✏️ Modifica trascrizione + sintesi (PATCH /agenda/memos/{id})
 *   • 🗑️ Elimina memo + i suoi to-do aperti (DELETE /agenda/memos/{id})
 *  Anche se l'AI di Whisper sbaglia un nome o una zona, l'agente può
 *  correggere senza dover rifare il vocale.
 */
function MemoRow({ memo, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [transcript, setTranscript] = useState(memo.transcript || "");
  const [summary, setSummary] = useState(memo.summary || "");
  const [busy, setBusy] = useState(false);
  const [reExtracting, setReExtracting] = useState(false);

  const transcriptChanged = transcript !== (memo.transcript || "");

  const save = async () => {
    if ((transcript || "").trim().length < 4) {
      toast.error("Trascrizione troppo corta");
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/agenda/memos/${memo.id}`, { transcript, summary });
      toast.success("Memo aggiornato");
      setEditing(false);
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e, "Errore salvataggio"));
    } finally {
      setBusy(false);
    }
  };

  /** Riestrai contatti + azioni dalla trascrizione corrente.
   *  Se l'agente ha appena corretto un nome (es. Bianconi → Bianchi),
   *  questo aggiorna anche i to-do aperti collegati. I to-do già fatti
   *  o ignorati restano nello storico. I contatti vecchi NON vengono
   *  cancellati automaticamente, ma se diventano "orfani" (non più
   *  riconosciuti) li mostriamo nel toast con un tap per rimuoverli.
   */
  const reExtract = async () => {
    if (transcriptChanged) {
      // Salva prima la nuova trascrizione, poi riestrai
      await save();
    }
    setReExtracting(true);
    try {
      const { data } = await api.post(`/agenda/memos/${memo.id}/re-extract`);
      const d = data?.diff || {};
      const parts = [];
      if (d.todos_removed) parts.push(`-${d.todos_removed} vecchi`);
      if (d.todos_added) parts.push(`+${d.todos_added} nuovi`);
      const detailParts = [];
      if (parts.length > 0) detailParts.push(`To-do: ${parts.join(" · ")}.`);
      const orphans = d.orphan_contacts || [];
      if (orphans.length > 0) {
        const names = orphans.map((o) => o.name).join(", ");
        detailParts.push(`Contatti vecchi non più riconosciuti: ${names}.`);
      }
      const detail = detailParts.length > 0 ? detailParts.join(" ") : "Estrazione aggiornata.";

      if (orphans.length > 0) {
        toast.success("Riestrazione completata", {
          description: detail,
          action: {
            label: `Elimina i ${orphans.length} contatti vecchi`,
            onClick: async () => {
              try {
                await Promise.all(
                  orphans.map((o) => api.delete(`/agenda/contacts/${o.id}`)),
                );
                toast.success(`${orphans.length} contatti eliminati`);
              } catch (err) {
                toast.error(apiError(err, "Errore eliminazione contatti"));
              }
              onChanged?.();
            },
          },
          duration: 12000,
        });
      } else {
        toast.success("Riestrazione completata", { description: detail, duration: 6000 });
      }
      setEditing(false);
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e, "Errore riestrazione"));
    } finally {
      setReExtracting(false);
    }
  };

  const remove = () => {
    toast(
      "Eliminare questo memo?",
      {
        description: "Anche i to-do aperti generati dal memo verranno rimossi.",
        action: {
          label: "Elimina",
          onClick: async () => {
            try {
              await api.delete(`/agenda/memos/${memo.id}`);
              toast.success("Memo eliminato");
              onChanged?.();
            } catch (e) {
              toast.error(apiError(e, "Errore eliminazione"));
            }
          },
        },
        cancel: { label: "Annulla", onClick: () => {} },
        duration: 8000,
      },
    );
  };

  return (
    <div className="bg-white border border-[#E8E5DE] rounded-lg p-3" data-testid={`memo-row-${memo.id}`}>
      <div className="flex items-center justify-between text-[10.5px] text-[#9A968B] uppercase tracking-[0.1em] mb-1.5">
        <span>{new Date(memo.created_at).toLocaleString("it-IT", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
        <div className="flex items-center gap-2">
          {memo.mood && <span className="italic normal-case">{memo.mood}</span>}
          {!editing && (
            <>
              <button
                onClick={() => setEditing(true)}
                className="p-1 text-[#9A968B] hover:text-[#1C1C1A] transition-colors"
                aria-label="Modifica"
                data-testid={`memo-edit-${memo.id}`}
              >
                <PencilSimple size={13} />
              </button>
              <button
                onClick={remove}
                className="p-1 text-[#9A968B] hover:text-red-600 transition-colors"
                aria-label="Elimina"
                data-testid={`memo-delete-${memo.id}`}
              >
                <Trash size={13} />
              </button>
            </>
          )}
        </div>
      </div>

      {editing ? (
        <div className="space-y-2">
          <input
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            placeholder="Sintesi (max 280)"
            maxLength={280}
            className="w-full px-2 py-1.5 text-[13.5px] font-medium border border-[#E8E5DE] rounded focus:outline-none focus:border-[#7C8B6E]"
            data-testid={`memo-edit-summary-${memo.id}`}
          />
          <textarea
            value={transcript}
            onChange={(e) => setTranscript(e.target.value)}
            rows={4}
            className="w-full px-2 py-1.5 text-[12px] text-[#6B6A65] leading-[1.55] border border-[#E8E5DE] rounded focus:outline-none focus:border-[#7C8B6E] resize-y"
            data-testid={`memo-edit-transcript-${memo.id}`}
          />
          {/* Bottone "Riestrai contatti/azioni": utile dopo aver corretto un
              nome o un dato sbagliato dalla generazione. Mostra subito il diff dei to-do.
              I contatti vecchi non vengono toccati (più sicuro). */}
          <button
            onClick={reExtract}
            disabled={busy || reExtracting}
            className="w-full inline-flex items-center justify-center gap-1.5 py-1.5 border border-[#E8E5DE] hover:border-[#7C8B6E] hover:bg-[#FAFAF7] rounded text-[11.5px] text-[#6B6A65] hover:text-[#1C1C1A] transition-colors disabled:opacity-50"
            data-testid={`memo-reextract-${memo.id}`}
            title={transcriptChanged
              ? "Salva la nuova trascrizione e ri-analizza"
              : "Ri-analizza la trascrizione e aggiorna i to-do"}
          >
            {reExtracting
              ? <><Spinner size={11} className="animate-spin" /> Riestraggo…</>
              : <><Sparkle size={11} weight="fill" className="text-[#7C8B6E]" /> Riestrai contatti e to-do{transcriptChanged ? " (salva + rigenera)" : ""}</>}
          </button>
          <div className="flex gap-2">
            <button
              onClick={() => { setEditing(false); setTranscript(memo.transcript || ""); setSummary(memo.summary || ""); }}
              className="flex-1 py-1.5 border border-[#E8E5DE] rounded text-[12px] text-[#9A968B] hover:bg-[#FAFAF7]"
              data-testid={`memo-edit-cancel-${memo.id}`}
            >
              Annulla
            </button>
            <button
              onClick={save}
              disabled={busy}
              className="flex-1 py-1.5 bg-[#7C8B6E] hover:bg-[#6B7A5D] text-white font-medium rounded text-[12px] disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
              data-testid={`memo-edit-save-${memo.id}`}
            >
              {busy ? <Spinner size={11} className="animate-spin" /> : <Check size={11} weight="bold" />}
              Salva
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="text-[13.5px] text-[#1C1C1A] font-medium">{memo.summary}</div>
          {memo.contact_names?.length > 0 && (
            <div className="mt-1 text-[11px] text-[#7C8B6E]">
              👥 {memo.contact_names.join(" · ")}
            </div>
          )}
          <details className="mt-1.5">
            <summary className="text-[11px] text-[#9A968B] cursor-pointer">testo completo</summary>
            <div className="mt-1.5 text-[12px] text-[#6B6A65] leading-[1.55] whitespace-pre-wrap">{memo.transcript}</div>
          </details>
        </>
      )}
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────
export default function Agenda() {
  const [tab, setTab] = useState("oggi");
  const [briefing, setBriefing] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [memoOpen, setMemoOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const c = searchParams.get("contact");
    if (c) { setTab("clienti"); setSelectedContact(c); }
  }, [searchParams]);

  const loadBriefing = async () => {
    try {
      const { data } = await api.get("/agenda/briefing");
      setBriefing(data);
    } catch (e) {
      // silent
    }
  };

  const loadContacts = async () => {
    try {
      const { data } = await api.get("/agenda/contacts");
      setContacts(data || []);
    } catch { /* silent */ }
  };

  useEffect(() => { loadBriefing(); loadContacts(); }, []);

  // iter v29 r213 — Pull-to-refresh: ricarica briefing + contatti
  const refreshAll = async () => {
    await Promise.all([loadBriefing(), loadContacts()]);
  };
  const { pullDistance, isRefreshing } = usePullToRefresh(refreshAll);

  const TABS = [
    { k: "oggi",       l: "Oggi",         Icon: CheckCircle,    hint: "Appuntamenti, scadenze e contatti caldi del giorno" },
    { k: "promemoria", l: "Da fare",      Icon: ListChecks,     hint: "Cose da fare e scadenze, anche dei prossimi giorni" },
    { k: "clienti",    l: "Da richiamare",Icon: Users,          hint: "Clienti seguiti, ordinati per priorità di richiamo" },
    { k: "timeline",   l: "Tutto",        Icon: ChatCircleDots, hint: "Storico completo dei tuoi memo vocali" },
  ];

  // iter v28 r221 — User M1141: pulsante "?" per riaprire la guida.
  const action = (
    <ReopenGuidesBtn keys={["agenda:3-strade"]} testId="agenda-reopen-guides" />
  );

  const isFirstTime = briefing && (briefing.stats?.contacts ?? 0) === 0 && (briefing.stats?.memos_last_7d ?? 0) === 0;

  return (
    <AppShell title="Agenda" breadcrumb="Agenda" action={action}>
      <PullIndicator pullDistance={pullDistance} isRefreshing={isRefreshing} />
      <div className="max-w-3xl mx-auto pb-24">
        {/* Hero "Parla a Pronto" — sempre visibile in cima all'agenda.
            È il CTA principale della pagina: l'agente apre, parla, Pronto
            registra/trascrive/cataloga. Per first-time users espandiamo
            con il copy esteso. Per utenti esistenti restiamo compatti.

            iter v30 r113 — User: «il tasto mic in agenda ingombra troppo
            su mobile, copre l'agenda». Hero rimpicciolito su mobile
            (mic 48→ rimane 64 desk, p ridotti, h1 più piccolo, mb-4). */}
        <div
          className="mb-4 sm:mb-6 bg-gradient-to-br from-[#7C8B6E] via-[#6B7A5D] to-[#5C6A4F] rounded-2xl p-3.5 sm:p-6 text-white shadow-lg shadow-[#7C8B6E]/15"
          data-testid="agenda-hero"
        >
          <div className="flex items-start gap-3 sm:gap-4">
            <button
              onClick={() => setMemoOpen(true)}
              className="shrink-0 w-12 h-12 sm:w-20 sm:h-20 rounded-full bg-white text-[#5C6A4F] hover:bg-[#F1F4ED] flex items-center justify-center transition-all hover:scale-105 active:scale-95 shadow-md ring-2 sm:ring-4 ring-white/20"
              aria-label="Parla a Pronto"
              data-testid="hero-memo-btn"
            >
              <Microphone size={22} weight="fill" className="sm:hidden" />
              <Microphone size={32} weight="fill" className="hidden sm:block" />
            </button>
            <div className="flex-1 min-w-0">
              <div className="text-[9.5px] sm:text-[10px] uppercase tracking-[0.22em] text-white/80 font-semibold mb-0.5 sm:mb-1">
                Parla a Pronto
              </div>
              <h1 className="text-[16px] sm:text-[24px] font-semibold leading-tight tracking-tight">
                {isFirstTime ? "Inizia parlando." : "Cosa devo ricordarmi?"}
              </h1>
              <p className="mt-1.5 sm:mt-2 text-[11.5px] sm:text-[12.5px] text-white/85 leading-snug sm:leading-relaxed">
                {isFirstTime
                  ? "Premi il microfono e di' tutto: appuntamenti, clienti, scadenze, idee. Pronto trascrive e organizza l'agenda al posto tuo."
                  : "Premi il microfono. Trascrivo, riconosco clienti e immobili, e metto tutto qui sotto."}
              </p>
              <div className="mt-2 sm:mt-3 flex items-center gap-2">
                <button
                  onClick={() => setMemoOpen(true)}
                  className="bg-white text-[#1C1C1A] hover:bg-[#F1F4ED] text-[11.5px] sm:text-[12.5px] font-semibold px-3 sm:px-4 py-1.5 sm:py-2 rounded-full inline-flex items-center gap-1.5 transition-colors"
                  data-testid="hero-memo-cta"
                >
                  <Microphone size={12} weight="fill" /> Parla
                </button>
                <button
                  onClick={() => setManualOpen(true)}
                  className="bg-white/15 text-white border border-white/30 hover:bg-white/25 text-[11.5px] sm:text-[12.5px] font-semibold px-3 sm:px-4 py-1.5 sm:py-2 rounded-full inline-flex items-center gap-1.5 transition-colors"
                  data-testid="hero-manual-cta"
                >
                  <Plus size={12} weight="bold" /> Scrivi
                </button>
              </div>
            </div>
          </div>
        </div>

        {!isFirstTime && (
          <div className="mb-3 flex items-baseline justify-between">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#7C8B6E] font-semibold">Le tue cose</div>
          </div>
        )}

        {/* Tab switcher */}
        <div className="flex items-center gap-1 mb-2 bg-[#F5F4F0] border border-[#E8E5DE] rounded-lg p-1 w-fit overflow-x-auto" data-testid="agenda-tabs">
          {TABS.map(({ k, l, Icon }) => (
            <button
              key={k}
              onClick={() => { setTab(k); if (k !== "clienti") setSelectedContact(null); }}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12.5px] font-medium transition-all ${
                tab === k
                  ? "bg-white text-[#1C1C1A] shadow-sm"
                  : "text-[#6B6A65] hover:text-[#1C1C1A]"
              }`}
              data-testid={`agenda-tab-${k}`}
            >
              <Icon size={13} weight={tab === k ? "fill" : "regular"} className={tab === k ? "text-[#7C8B6E]" : ""} />
              {l}
            </button>
          ))}
        </div>
        {/* Tab hint sentence */}
        <p className="text-[11.5px] text-[#9A968B] mb-5 px-1" data-testid="agenda-tab-hint">
          {(TABS.find((t) => t.k === tab) || {}).hint}
        </p>

        {tab === "oggi" && (
          <TabOggi
            briefing={briefing}
            onUpdate={loadBriefing}
            onboarding={briefing && (briefing.stats?.contacts ?? 0) === 0 && (briefing.stats?.memos_last_7d ?? 0) === 0}
            onTryExample={() => { toast.success("Memo di esempio creato"); loadBriefing(); loadContacts(); }}
          />
        )}
        {tab === "clienti" && <TabClienti contacts={contacts} onRefresh={loadContacts} selected={selectedContact} setSelected={setSelectedContact} />}
        {tab === "promemoria" && <TabPromemoria onUpdate={loadBriefing} />}
        {tab === "timeline" && <TabTimeline />}
      </div>

      {/* iter 275: rimossi i 2 FAB ridondanti (memo + manual). L'hero in cima
          ha già microfono grande + CTA Parla/Scrivi. L'utente lamentava che
          coprivano la pagina e duplicavano la stessa azione. */}

      <MemoRecorder
        open={memoOpen}
        onClose={() => setMemoOpen(false)}
        onCreated={() => {
          toast.success("Memo salvato");
          loadBriefing(); loadContacts();
        }}
      />
      <ManualEventModal
        open={manualOpen}
        onClose={() => setManualOpen(false)}
        contacts={contacts}
        onCreated={() => { loadBriefing(); }}
      />
    </AppShell>
  );
}
